import React from 'react';

// ─── Font + colour primitives shared by the docs-style pages ─────────
// Inter body, Outfit headings, JetBrains Mono code (Inter/JetBrains
// loaded in index.html; Outfit loaded site-wide).
export const fontSans =
  "font-[Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif]";
export const fontHeading = 'font-[Outfit]';
export const fontMono =
  "font-['JetBrains_Mono',ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,'Liberation_Mono',monospace]";

export const linkClass =
  'text-stone-700 underline decoration-stone-400 underline-offset-2 hover:text-stone-900';

// ─── Block-level typography ──────────────────────────────────────────

export function DocH1({ children }: { children: React.ReactNode }) {
  return (
    <h1
      className={`
        mb-1 text-4xl font-bold tracking-tight text-stone-900
        sm:text-[2.5rem]
        ${fontHeading}
      `}
    >
      {children}
    </h1>
  );
}

export function DocSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <p
      className={`
        mb-8 text-lg font-medium tracking-tight text-stone-500
        sm:text-xl
      `}
    >
      {children}
    </p>
  );
}

export function DocH2({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  return (
    <h2
      id={id}
      className={`
        scroll-mt-24 border-b border-stone-100 pb-2 text-2xl font-semibold
        tracking-tight text-stone-900
        ${fontHeading}
      `}
    >
      {children}
    </h2>
  );
}

export function DocH3({ children }: { children: React.ReactNode }) {
  return (
    <h3
      className={`
        mt-6 mb-2 text-base font-semibold text-stone-900
        ${fontHeading}
      `}
    >
      {children}
    </h3>
  );
}

export function DocP({ children }: { children: React.ReactNode }) {
  return (
    <p
      className={`
        mb-3 text-[15px] leading-relaxed text-stone-700
        last:mb-0
        sm:text-base
      `}
    >
      {children}
    </p>
  );
}

export function DocUl({ children }: { children: React.ReactNode }) {
  return (
    <ul
      className={`
        mb-4 list-disc space-y-2 pl-6 text-[15px] leading-relaxed text-stone-700
        sm:text-base
      `}
    >
      {children}
    </ul>
  );
}

export function DocOl({ children }: { children: React.ReactNode }) {
  return (
    <ol
      className={`
        mb-4 list-decimal space-y-2 pl-6 text-[15px] leading-relaxed
        text-stone-700
        sm:text-base
      `}
    >
      {children}
    </ol>
  );
}

export function DocHr() {
  return <hr className="my-10 border-0 border-t border-stone-200" />;
}

// ─── Callout / inline / code-block primitives ────────────────────────

export function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div
      className={`
        my-4 rounded-r-md border-l-[3px] border-amber-300 bg-amber-50/80 px-4
        py-3 text-[15px] leading-relaxed text-stone-800
      `}
    >
      {children}
    </div>
  );
}

export function InlineCode({ children }: { children: React.ReactNode }) {
  return (
    <code
      className={`
        rounded bg-[#f1f0ed] px-1.5 py-0.5 text-[0.8125rem] text-stone-800
        ring-1 ring-stone-200/80
        [font-feature-settings:'liga'_0]
        ${fontMono}
      `}
    >
      {children}
    </code>
  );
}

export function CodeBlock({ children }: { children: string }) {
  return (
    <pre
      className={`
        mb-4 overflow-x-auto rounded-md border border-stone-200 bg-[#f7f6f3] p-4
        text-[13px] leading-relaxed text-stone-800 shadow-inner
        sm:text-sm
        ${fontMono}
      `}
    >
      <code>{children}</code>
    </pre>
  );
}

export function DocTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: React.ReactNode[][];
}) {
  return (
    <div className="mb-6 overflow-x-auto rounded-md border border-stone-200">
      <table className="w-full min-w-[320px] border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-stone-200 bg-[#f7f6f3]">
            {headers.map((h) => (
              <th
                key={h}
                className={`
                  px-3 py-2.5 font-medium whitespace-nowrap text-stone-700
                `}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100 bg-white">
          {rows.map((cells, i) => (
            <tr key={i} className="text-stone-700">
              {cells.map((cell, j) => (
                <td key={j} className="px-3 py-2.5 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Wrapper for the tutorial's screenshots / short loop videos. Same
// border / shadow language as `CodeBlock` so figures sit consistently
// inside the article column.
export function DocMediaFrame({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`
        my-4 overflow-hidden rounded-md border border-stone-200 bg-white
        shadow-[0_1px_3px_rgba(0,0,0,0.06)]
        ${className}
      `}
    >
      {children}
    </div>
  );
}
